import { z } from 'zod';

/** Comment threads on campaigns and Canvas (BB-21); a Canvas thread may be pinned to a step. */
export const COMMENT_OBJECT_TYPES = ['campaign', 'canvas'] as const;
export type CommentObjectType = (typeof COMMENT_OBJECT_TYPES)[number];
export const COMMENT_BODY_MAX = 5000;

const Body = z.string().trim().min(1).max(COMMENT_BODY_MAX);
const Mentions = z.array(z.uuid()).max(20).default([]);

export const CommentThreadQuery = z.object({ status: z.enum(['open', 'resolved', 'all']).default('open') });
export const CreateThreadBody = z.strictObject({ body: Body, mentions: Mentions, anchor: z.strictObject({ stepId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/) }).nullable().default(null) });
export type CreateThreadBody = z.infer<typeof CreateThreadBody>;
export const AddCommentBody = z.strictObject({ body: Body, mentions: Mentions });
export type AddCommentBody = z.infer<typeof AddCommentBody>;
export const EditCommentBody = z.strictObject({ body: Body, mentions: Mentions });
export const MentionQuery = z.object({ query: z.string().trim().max(100).default('') });

export interface CommentPerson {
  id: string;
  email: string | null;
  name: string | null;
}

export interface CommentView {
  id: string;
  author: CommentPerson | null;
  /** Empty when deleted. */
  body: string;
  mentions: CommentPerson[];
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
}

export interface CommentThreadView {
  id: string;
  objectType: CommentObjectType;
  objectId: string;
  anchor: { stepId: string } | null;
  status: 'open' | 'resolved';
  createdBy: CommentPerson | null;
  createdAt: string;
  resolvedBy: CommentPerson | null;
  resolvedAt: string | null;
  comments: CommentView[];
}

export interface CommentThreadList {
  items: CommentThreadView[];
  /** Open threads per Canvas step (for the counters on nodes) and on the object itself (`null` key → `object`). */
  openByStep: Record<string, number>;
  openOnObject: number;
}
